import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  ArrowRight,
  CheckCircle,
  Laptop,
  Settings,
  Users,
  TrendingUp,
  Building2,
  Heart,
  Zap,
  BarChart3,
  RefreshCw,
  GraduationCap,
} from "lucide-react";
import SEO from "../components/SEO";
import StructuredData from "../components/StructuredData";
import MidPageCTA from "../components/MidPageCTA";
import SectionDivider from "../components/SectionDivider";

const EhrImplementation = () => {
  const outcomes = [
    "Smoother EHR go-live with fewer disruptions to patient care",
    "Staff that actually knows how to use the system — not just click around",
    "Streamlined clinical workflows that save hours every week",
    "Better data accuracy and fewer documentation errors",
    "Reduced administrative burden on your front desk and clinical staff",
    "Telehealth and practice management tools that work together seamlessly",
    "A system that supports your growth, not slows it down",
  ];

  const whoIsFor = [
    { icon: Building2, label: "Private practices selecting or switching EHR systems" },
    { icon: Heart, label: "Clinics struggling with a poorly configured EHR" },
    { icon: Users, label: "Home healthcare agencies integrating clinical and billing systems" },
    { icon: TrendingUp, label: "Healthcare startups choosing their first technology stack" },
  ];

  return (
    <div>
      <SEO
        title="EHR Implementation & Healthcare Technology Support"
        description="Need help selecting, implementing, or optimizing your EHR system? RNB Consulting Group provides hands-on EHR implementation support, workflow optimization, and staff training for healthcare practices."
        keywords="ehr implementation help, how to implement new ehr system, healthcare technology consulting, ehr transition support, practice management system help, telehealth integration, ehr training for staff, medical practice software help"
      />
      <StructuredData data={[{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.rnbconsultinggroup.com" },
          { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://www.rnbconsultinggroup.com/services" },
          { "@type": "ListItem", "position": 3, "name": "EHR Implementation", "item": "https://www.rnbconsultinggroup.com/services/ehr-implementation" }
        ]
      }]} />

      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 bg-gradient-to-br from-rnb-navy via-rnb-navy to-rnb-blue overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-20 w-72 h-72 bg-rnb-teal/15 rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-10 w-96 h-96 bg-rnb-blue/20 rounded-full blur-3xl" />
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div className="text-white">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-8">
                <Laptop className="w-4 h-4 text-rnb-teal" />
                <span className="text-sm font-medium">Healthcare Technology & EHR Support</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6" data-testid="ehr-hero-headline">
                Your EHR Should Make Your Practice
                <span className="block mt-2 bg-gradient-to-r from-rnb-teal to-cyan-300 bg-clip-text text-transparent">
                  Run Better, Not Slower
                </span>
              </h1>

              <p className="text-lg sm:text-xl text-white/80 leading-relaxed mb-8">
                Whether you're selecting a new system, switching from one that isn't working, or trying to get more out of what you already have — we guide the entire process so your team isn't left figuring it out alone.
              </p>

              <ul className="flex flex-wrap gap-x-6 gap-y-3 mb-10 text-white/90 text-sm sm:text-base">
                <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-rnb-teal flex-shrink-0" /> EHR selection & implementation</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-rnb-teal flex-shrink-0" /> Workflow optimization</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-rnb-teal flex-shrink-0" /> Staff training & adoption</li>
              </ul>

              <Link to="/consultation">
                <Button
                  size="lg"
                  className="bg-rnb-teal hover:bg-rnb-teal/90 text-white rounded-full px-8 py-6 text-lg font-semibold shadow-glow hover:shadow-glow-lg transition-all duration-300 hover:-translate-y-1"
                  data-testid="ehr-hero-cta"
                >
                  Schedule a Free Consultation
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>

            <div className="relative">
              <img
                src="/images/healthcare/nurse-at-computer.jpg"
                alt="Healthcare professional using EHR system on computer"
                className="rounded-2xl shadow-2xl w-full h-56 sm:h-72 lg:h-auto object-cover border-2 border-white/10"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Problem Acknowledgment */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-rnb-navy mb-4">
              Technology Should Be Helping, Not Hurting
            </h2>
            <p className="text-rnb-gray text-lg max-w-2xl mx-auto">
              We hear these frustrations from healthcare leaders every week — and they're all fixable.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {[
              "Your current EHR feels clunky, slow, or like it was built for a different kind of practice",
              "Staff spends more time clicking through screens than caring for patients",
              "You're about to switch systems but the thought of migration keeps you up at night",
              "Telehealth and practice management tools don't talk to each other",
              "Your team was never properly trained — they use workarounds instead of workflows",
              "You're not sure which system is actually right for your practice's needs and budget",
            ].map((problem, idx) => (
              <div key={problem.slice(0, 40)} className="flex items-start gap-3 p-4 rounded-xl bg-slate-50 border border-slate-100">
                <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-red-500 text-xs font-bold">{idx + 1}</span>
                </div>
                <p className="text-rnb-gray leading-relaxed">{problem}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How We Help */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-rnb-navy mb-4">
              How We Help You Get Technology Right
            </h2>
            <p className="text-rnb-gray text-lg max-w-2xl mx-auto">
              We're not a software company. We're your operational partner — helping you select, implement, and optimize the tools your practice actually needs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: BarChart3, title: "EHR Selection & Evaluation", desc: "We help you evaluate systems based on your practice's workflows, size, specialty, and budget — not just what's popular or what a sales rep is pushing." },
              { icon: Settings, title: "Implementation Planning", desc: "We manage the entire implementation process — timelines, milestones, data migration, configuration — so your team isn't left to figure it out alone." },
              { icon: RefreshCw, title: "Data Migration & Transition", desc: "Switching systems? We plan and oversee data migration so nothing gets lost and your team transitions smoothly with minimal disruption." },
              { icon: Zap, title: "Workflow Optimization", desc: "We map your clinical and administrative workflows to your system's capabilities — eliminating workarounds and making sure the technology actually supports how you work." },
              { icon: GraduationCap, title: "Staff Training & Adoption", desc: "We train your team role by role — front desk, clinical staff, billing — so everyone knows how to use the system properly, not just 'enough to get by.'" },
              { icon: Laptop, title: "System Integration", desc: "We help connect your EHR with telehealth platforms, billing software, patient portals, and other tools so your systems work together seamlessly." },
            ].map((item, idx) => (
              <Card key={item.title || item.name || item.label || idx} className="border-0 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-7">
                  <div className="w-12 h-12 bg-rnb-light rounded-xl flex items-center justify-center mb-5">
                    <item.icon className="w-6 h-6 text-rnb-blue" />
                  </div>
                  <h3 className="text-lg font-bold text-rnb-navy mb-2">{item.title}</h3>
                  <p className="text-rnb-gray text-sm leading-relaxed">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Mid-page CTA */}
      <MidPageCTA variant={3} image="/images/healthcare/staff-dual-monitors.jpg" />

      {/* Outcomes */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-rnb-navy mb-4">
              What You Can Expect
            </h2>
            <p className="text-rnb-gray text-lg max-w-2xl mx-auto">
              When technology is implemented right, everything in your practice gets a little easier.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {outcomes.map((outcome, idx) => (
              <div key={outcome.slice(0, 40)} className="flex items-start gap-3 p-4">
                <CheckCircle className="w-5 h-5 text-rnb-teal flex-shrink-0 mt-0.5" />
                <span className="text-rnb-navy font-medium">{outcome}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-rnb-navy mb-4">
              Who This Is For
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {whoIsFor.map((item, idx) => (
              <div key={item.title || item.name || item.label || idx} className="flex items-center gap-4 p-5 bg-white rounded-xl shadow-sm">
                <div className="w-11 h-11 bg-rnb-light rounded-xl flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-rnb-blue" />
                </div>
                <span className="text-rnb-navy font-medium">{item.label}</span>
              </div>
            ))}
          </div>

          <p className="text-center text-rnb-gray mt-8 text-sm">
            <strong className="text-rnb-navy">Important:</strong> We do not sell software. We help you select, implement, and optimize systems operationally — as your independent advisor.
          </p>
        </div>
      </section>

      {/* Why RNB */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-rnb-navy mb-4">
              Why Healthcare Practices Choose RNB
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {[
              { icon: Heart, title: "Healthcare-Specific Expertise", desc: "We understand the unique technology needs of medical practices — from specialty-specific EHR features to compliance requirements your system needs to support." },
              { icon: Settings, title: "Vendor-Neutral Guidance", desc: "We don't sell software or take commissions from vendors. Our recommendations are based solely on what's best for your practice." },
              { icon: GraduationCap, title: "Training That Sticks", desc: "We don't do a one-hour demo and leave. We train your team hands-on, role by role, until they're confident and the system is actually being used correctly." },
              { icon: TrendingUp, title: "Long-Term Optimization", desc: "After go-live, we help you continue optimizing workflows as your practice evolves — so your investment keeps paying off." },
            ].map((item, idx) => (
              <div key={item.title || item.name || item.label || idx} className="flex items-start gap-4 p-5">
                <div className="w-10 h-10 bg-rnb-teal/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-rnb-teal" />
                </div>
                <div>
                  <h3 className="font-bold text-rnb-navy mb-1">{item.title}</h3>
                  <p className="text-rnb-gray text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-rnb-navy to-rnb-blue relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-rnb-teal/15 rounded-full blur-3xl" />
        </div>

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Don't Let the Wrong Technology Hold Your Practice Back
          </h2>
          <p className="text-lg text-white/80 mb-10 max-w-2xl mx-auto">
            Whether you're selecting your first EHR, switching to a better one, or just need help getting your current system to actually work for you — we can help. Let's talk about where you are and where you want to be.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/consultation">
              <Button
                size="lg"
                className="bg-white text-rnb-navy hover:bg-white/90 rounded-full px-8 py-6 text-lg font-semibold shadow-lg hover:-translate-y-1 transition-all duration-300"
                data-testid="ehr-bottom-cta"
              >
                Book a Free Consultation
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/quote">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-semibold transition-all duration-300"
              >
                Get an Instant Quote
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EhrImplementation;
